<template>
  <div class="flex flex-col gap-y-4 py-2 form-step">
    <!--
      M2, balão ⑥ — quando o wizard é aberto pelo reencaminhamento do modal "Enviar registro",
      os campos que vêm da vaga chegam preenchidos e bloqueados. Sem `lockedFields` nada muda:
      o wizard de folha continua exatamente como sempre foi.
    -->
    <p
      v-if="lockedFields.length"
      class="rounded-lg bg-primary5/40 border border-primary30 px-3 py-2 text-xs text-textPrimary"
      data-testid="attendance-locked-fields-notice"
    >
      Empresa, Unidade, Data do plantão e Escala vêm da vaga e ficam bloqueados. Para enviar outra
      folha, use "Novo registro" na fila.
    </p>

    <!--
      `value-comparator`: as stores publicam as opções com `value: String(id)` e o prefill da
      vaga chega com o id numérico da API — sem tolerância o Vuetify não acha o item e imprime
      o id cru no lugar do nome da empresa/unidade.
    -->
    <v-autocomplete
      v-model="localForm.company"
      label="Empresa"
      :items="listCompanies"
      item-title="title"
      item-value="value"
      :value-comparator="isSameId"
      variant="outlined"
      density="compact"
      hide-details="auto"
      color="primary20"
      :disabled="isLocked('company')"
      data-testid="attendance-company-select"
      @update:model-value="handleCompanyChange"
    />

    <v-autocomplete
      v-model="localForm.healthUnit"
      label="Unidade"
      :items="listHealthUnitsByCompany"
      item-title="title"
      item-value="value"
      :value-comparator="isSameId"
      variant="outlined"
      density="compact"
      hide-details="auto"
      color="primary20"
      :disabled="isLocked('healthUnit') || !localForm.company"
      :loading="isLoadingUnits"
      data-testid="attendance-unit-select"
      @update:model-value="handleUnitChange"
    />

    <DatePicker v-model="localForm.date" locale="pt-BR" :masks="{ input: 'DD/MM/YYYY' }">
      <template #default="{ inputValue, inputEvents }">
        <v-text-field
          label="Data do plantão"
          placeholder="DD/MM/AAAA"
          :model-value="inputValue"
          v-on="inputEvents"
          @input="lastTypedDateString = ($event.target as HTMLInputElement).value"
          density="compact"
          variant="outlined"
          hide-details="auto"
          :class="['w-full', { 'date-field-error': dateError }]"
          color="primary20"
          :disabled="isLocked('date') || !localForm.healthUnit"
          data-testid="attendance-date-input"
        />
      </template>
    </DatePicker>

    <v-autocomplete
      v-model="localForm.schedules"
      label="Escala(s) - Modalidade de registro de plantão"
      :items="enrichedSchedules"
      variant="outlined"
      density="compact"
      color="primary20"
      multiple
      chips
      closable-chips
      :disabled="isLocked('schedules') || !localForm.healthUnit || !localForm.date"
      :loading="isLoadingSchedules"
      :menu-props="{ contentClass: 'ds-attendance-schedules-dropdown' }"
      :error-messages="
        hasMultipleMonitoringTypes
          ? 'Não é permitido selecionar escalas com modalidades diferentes.'
          : undefined
      "
      :hide-details="!hasMultipleMonitoringTypes ? 'auto' : false"
      data-testid="attendance-schedules-select"
      @update:model-value="handleSchedulesChange"
    />

    <div
      v-if="modalitiesLoadFailed"
      class="flex items-center gap-2 text-sm"
      style="color: rgb(var(--v-theme-error))"
    >
      <span>Erro ao carregar escalas.</span>
      <button
        type="button"
        class="retry-link"
        data-testid="attendance-modalities-retry"
        @click="fetchSchedulesWithMonitoringTypes"
      >
        Tente novamente
      </button>
    </div>

    <v-autocomplete
      v-model="localForm.shifts"
      :label="shiftsLabel"
      :items="shiftsWithScheduleName"
      variant="outlined"
      density="compact"
      hide-details="auto"
      color="primary20"
      multiple
      chips
      closable-chips
      :disabled="isLocked('shifts') || !localForm.schedules.length"
      :loading="isLoadingShifts"
      :aria-label="isNonDailySheet ? 'Turno(s), opcional para Folha não diária' : 'Turno(s)'"
      data-testid="attendance-shifts-select"
    />

    <v-autocomplete
      v-model="localForm.uploadedBy"
      label="Escalista responsável"
      :items="allocators"
      variant="outlined"
      density="compact"
      hide-details="auto"
      color="primary20"
      :disabled="isLocked('uploadedBy') || !localForm.schedules.length"
      :loading="isLoadingAllocators"
      data-testid="attendance-allocator-select"
    />

    <div
      v-if="allocatorsLoadFailed"
      class="flex items-center gap-2 text-sm"
      style="color: rgb(var(--v-theme-error))"
    >
      <span>Erro ao carregar os escalistas responsáveis.</span>
      <button
        type="button"
        class="retry-link"
        data-testid="attendance-allocators-retry"
        @click="retryAllocators"
      >
        Tente novamente
      </button>
    </div>

    <v-autocomplete
      v-model="localForm.imageState"
      label="Estado do registro"
      :items="imageStateOptions"
      item-title="label"
      item-value="value"
      variant="outlined"
      density="compact"
      hide-details="auto"
      color="primary20"
      data-testid="attendance-image-state-select"
    />

    <DsSwitch
      v-model="localForm.uploadAnother"
      label="Upload de novo registro após esse"
      size="small"
      data-testid="attendance-upload-another-switch"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import * as yup from 'yup'
import { DatePicker } from 'v-calendar'
import { useToast } from 'vue-toastification'
import type { ActiveModality } from '@/types/ds-attendance.types'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import {
  ImageState,
  DSAttendanceMonitoringType,
  MONITORING_TYPE_LABELS,
  formatMonitoringTypeLabel
} from '@/constants/ds-attendance.constants'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { useCompanyStore } from '@/pinia-store/useCompanyStore'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import { useShiftStore } from '@/pinia-store/useShiftStore'
import ScheduleUnitService from '@/Network/Services/ScheduleUnitService'
import Network from '@/Network'
import { mapConcurrent } from '@/utils/async'
import { isSameId, toId } from '@/utils/selectId'
import { useErrorToast } from '@/composables/useErrorToast'
import type { DsAttendanceUploadForm } from './types'

const props = withDefaults(
  defineProps<{
    modelValue: DsAttendanceUploadForm
    /**
     * Campos que chegam da vaga de origem e não podem ser trocados aqui (M2).
     * Vazio por padrão: o wizard aberto pela fila segue com tudo editável.
     */
    lockedFields?: string[]
  }>(),
  { lockedFields: () => [] }
)

const isLocked = (field: string): boolean => props.lockedFields.includes(field)

const emit = defineEmits<{
  'update:modelValue': [value: DsAttendanceUploadForm]
  'update:valid': [value: boolean]
}>()

const localForm = ref({ ...props.modelValue })

watch(
  () => props.modelValue,
  (newVal) => {
    localForm.value = { ...newVal }
  }
)

watch(
  localForm,
  (newVal) => {
    emit('update:modelValue', newVal)
  },
  { deep: true }
)

const activeModalities = ref<ActiveModality[]>([])
const modalitiesLoadFailed = ref(false)

const hasMultipleMonitoringTypes = computed(() => {
  const selectedIds = new Set(localForm.value.schedules.map(String))
  const types = activeModalities.value
    .filter((m) => selectedIds.has(String(m.scheduleId)) && m.monitoringType !== null)
    .map((m) => m.monitoringType)
  return new Set(types).size > 1
})

const isNonDailySheet = computed(
  () => localForm.value.monitoringType === DSAttendanceMonitoringType.NON_DAILY_SHEET
)

const isValid = computed(() => {
  const f = localForm.value
  return !!(
    f.company &&
    f.healthUnit &&
    f.schedules.length &&
    // Shifts são opcionais para Folhas não diárias, obrigatórios para diárias.
    // Lógica: se for non-daily OU tiver shifts selecionados, é válido.
    (isNonDailySheet.value || f.shifts.length) &&
    f.uploadedBy &&
    f.date &&
    f.imageState &&
    !hasMultipleMonitoringTypes.value
  )
})

watch(isValid, (val) => emit('update:valid', val), { immediate: true })

const authStore = useAuthStore()
const companyStore = useCompanyStore()
const healthUnitStore = useHealthUnitStore()
const shiftStore = useShiftStore()

const toast = useToast()
const { notifyError } = useErrorToast()

const { listCompanies } = storeToRefs(companyStore)
const { listHealthUnitsByCompany } = storeToRefs(healthUnitStore)
const { listShifts } = storeToRefs(shiftStore)
const allocators = ref<{ title: string; value: string | number }[]>([])
const allocatorsLoadFailed = ref(false)

const enrichedSchedules = computed(() => {
  if (isLoadingSchedules.value) return []
  return activeModalities.value.map((m) => {
    const label = m.monitoringType
      ? (MONITORING_TYPE_LABELS[m.monitoringType as DSAttendanceMonitoringType] ?? m.monitoringType)
      : null
    return {
      title: label
        ? `${m.scheduleName} (${label})`
        : `${m.scheduleName} (Sem política de registro vigente!)`,
      value: m.scheduleId,
      props: {
        disabled:
          m.monitoringType === DSAttendanceMonitoringType.MINHA_ESCALA_APP || !m.monitoringType
      }
    }
  })
})

const scheduleNameById = computed(() => {
  const map = new Map<number, string>()
  for (const m of activeModalities.value) {
    map.set(m.scheduleId, m.scheduleName)
  }
  return map
})

const shiftsWithScheduleName = computed(() => {
  const selectedScheduleIds = new Set(localForm.value.schedules.map(Number))
  return listShifts.value
    .filter((shift) => selectedScheduleIds.has(shift.schedule?.id ?? NaN))
    .map((shift) => {
      const scheduleName = scheduleNameById.value.get(shift.schedule?.id)
      return {
        title: scheduleName ? `${shift.name} (${scheduleName})` : shift.name,
        value: shift.id
      }
    })
})

const shiftsLabel = computed(() => (isNonDailySheet.value ? 'Turno(s) (opcional)' : 'Turno(s)'))

const imageStateOptions = [
  { label: 'Completo', value: ImageState.COMPLETE },
  { label: 'Ilegível', value: ImageState.ILLEGIBLE },
  { label: 'Pendente', value: ImageState.PENDING }
]

const handleCompanyChange = async (val: string | number | null) => {
  localForm.value.healthUnit = null
  localForm.value.date = null
  resetScheduleDependents()
  if (!val) return
  isLoadingUnits.value = true
  try {
    await healthUnitStore.setHealthUnitsByCompany(+val)
  } finally {
    isLoadingUnits.value = false
  }
}

const getReferenceDateString = (): string | undefined => {
  const date = localForm.value.date
  if (!date) return undefined
  const str = String(date)
  const d =
    date instanceof Date
      ? date
      : new Date(/^\d{4}-\d{2}-\d{2}$/.test(str) ? str + 'T00:00:00' : str)
  if (isNaN(d.getTime())) return undefined
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

let fetchRequestId = 0

const resetScheduleDependents = () => {
  fetchRequestId++
  localForm.value.schedules = []
  localForm.value.shifts = []
  localForm.value.uploadedBy = null
  allocators.value = []
  allocatorsLoadFailed.value = false
  localForm.value.monitoringType = ''
  localForm.value.monitoringTypeLabel = ''
  activeModalities.value = []
  modalitiesLoadFailed.value = false
  isLoadingSchedules.value = false
}

const isLoadingUnits = ref(false)
const isLoadingSchedules = ref(false)
const isLoadingShifts = ref(false)
const isLoadingAllocators = ref(false)

const fetchSchedulesWithMonitoringTypes = async () => {
  resetScheduleDependents()
  const healthUnitId = localForm.value.healthUnit
  const referenceDate = getReferenceDateString()
  if (!healthUnitId || !referenceDate) return
  // resetScheduleDependents() já incrementou fetchRequestId; capturar valor atual
  const currentRequestId = fetchRequestId
  isLoadingSchedules.value = true
  modalitiesLoadFailed.value = false
  try {
    const list = await Network.dsAttendance.getActiveModalities(healthUnitId, referenceDate)
    if (currentRequestId !== fetchRequestId) return
    activeModalities.value = list
  } catch {
    if (currentRequestId !== fetchRequestId) return
    modalitiesLoadFailed.value = true
  } finally {
    if (currentRequestId === fetchRequestId) isLoadingSchedules.value = false
  }
}

const handleUnitChange = async (val: string | number | null) => {
  if (!val) {
    resetScheduleDependents()
    return
  }
  await fetchSchedulesWithMonitoringTypes()
}

const fetchAllocatorsFromSchedules = async (scheduleIds: number[]): Promise<void> => {
  const service = new ScheduleUnitService()
  let firstError: unknown = null

  allocatorsLoadFailed.value = false
  const allUsers = await mapConcurrent(scheduleIds, 5, async (id) => {
    try {
      return await service.stationCreatingUsers(id)
    } catch (error) {
      // fallback intencional: uma escala que falha não pode zerar os escalistas
      // das outras escalas selecionadas. O erro vira estado visível
      // (`allocatorsLoadFailed`) + toast logo abaixo, para a lista parcial não
      // ser lida como "só existem estes escalistas".
      if (firstError === null) firstError = error
      return []
    }
  })

  if (firstError !== null) {
    allocatorsLoadFailed.value = true
    notifyError(firstError, 'Não foi possível carregar os escalistas responsáveis.')
  }

  const seen = new Set<string | number>()
  allocators.value = allUsers
    .flat()
    .reduce<{ title: string; value: string | number }[]>((acc, user) => {
      if (!seen.has(user.id)) {
        seen.add(user.id)
        acc.push({ title: user.name, value: user.id })
      }
      return acc
    }, [])
}

const retryAllocators = async () => {
  const scheduleIds = (localForm.value.schedules ?? []).map(Number)
  if (!scheduleIds.length) return

  isLoadingAllocators.value = true
  try {
    await fetchAllocatorsFromSchedules(scheduleIds)
  } finally {
    isLoadingAllocators.value = false
  }
}

const setMonitoringTypeFromCache = () => {
  const schedules = localForm.value.schedules
  if (!schedules?.length) return

  const firstId = Number(schedules[0])
  const found = activeModalities.value.find((m) => m.scheduleId === firstId)
  if (found?.monitoringType) {
    localForm.value.monitoringType = found.monitoringType
    localForm.value.monitoringTypeLabel = formatMonitoringTypeLabel(
      found.monitoringType,
      found.externalApp
    )
  } else {
    localForm.value.monitoringType = ''
    localForm.value.monitoringTypeLabel = ''
  }
}

const INVALID_DATE_ERROR_MESSAGE = 'A data informada é inválida.'

const isValidDate = (dateValue: Date | string | null | undefined): boolean => {
  if (!dateValue) return true

  if (dateValue instanceof Date) {
    return !isNaN(dateValue.getTime())
  }

  const str = typeof dateValue === 'string' ? dateValue : ''
  const date = new Date(str)
  if (isNaN(date.getTime())) return false

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split('-').map(Number)
    const constructed = new Date(year, month - 1, day)
    return (
      constructed.getFullYear() === year &&
      constructed.getMonth() === month - 1 &&
      constructed.getDate() === day
    )
  }

  return true
}

const dateValidationSchema = yup.object().shape({
  date: yup
    .mixed<Date | string | null>()
    .test('is-valid-date', INVALID_DATE_ERROR_MESSAGE, (value) => isValidDate(value))
})

let previousValidDate: Date | string | null = props.modelValue.date
let isRevertingDate = false
const dateError = ref(false)
const lastTypedDateString = ref('')

const detectRollover = (dateValue: Date | string | null, typedString: string): boolean => {
  if (!typedString || !dateValue || typeof dateValue !== 'object' || !('getDate' in dateValue))
    return false

  const ddmmyyyyRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/
  const match = typedString.match(ddmmyyyyRegex)
  if (!match) return false

  const typedDay = parseInt(match[1], 10)
  const typedMonth = parseInt(match[2], 10)
  const typedYear = parseInt(match[3], 10)

  const actualDay = dateValue.getDate()
  const actualMonth = dateValue.getMonth() + 1
  const actualYear = dateValue.getFullYear()

  return typedDay !== actualDay || typedMonth !== actualMonth || typedYear !== actualYear
}

const validateDateValue = async (dateValue: Date | string | null, typedString: string = '') => {
  if (!dateValue) {
    previousValidDate = null
    dateError.value = false
    return true
  }

  if (detectRollover(dateValue, typedString)) {
    dateError.value = true
    toast.error(INVALID_DATE_ERROR_MESSAGE)
    return false
  }

  try {
    await dateValidationSchema.validate({ date: dateValue })
    previousValidDate = dateValue
    dateError.value = false
    return true
  } catch {
    dateError.value = true
    toast.error(INVALID_DATE_ERROR_MESSAGE)
    return false
  }
}

watch(
  () => localForm.value.date,
  async (newDate) => {
    if (isRevertingDate) {
      isRevertingDate = false
      return
    }

    const isDateValid = await validateDateValue(newDate, lastTypedDateString.value)
    if (!isDateValid) {
      isRevertingDate = true
      localForm.value.date = previousValidDate
    } else {
      fetchSchedulesWithMonitoringTypes()
    }
  },
  { flush: 'post' }
)

let scheduleChangeId = 0

const isStaleChange = (id: number) => id !== scheduleChangeId

const autoSelectLoggedAllocator = () => {
  const loggedUserId = authStore.getUserId
  if (allocators.value.some((a) => a.value === loggedUserId)) {
    localForm.value.uploadedBy = loggedUserId
  }
}

const resolveMonitoringType = () => {
  if (!localForm.value.monitoringType) setMonitoringTypeFromCache()
}

// Carrega turnos para as escalas selecionadas. Ao contrário do nome anterior (fetchShiftsIfNeeded),
// agora é sempre chamado, pois queremos limpar e recarregar quando escalas mudam.
const fetchShifts = async (scheduleIds: number[], changeId: number): Promise<void> => {
  isLoadingShifts.value = true
  try {
    await shiftStore.setShiftsBySchedules(scheduleIds)
  } finally {
    if (!isStaleChange(changeId)) isLoadingShifts.value = false
  }
}

const handleSchedulesChange = async (val: (string | number)[]) => {
  const currentChangeId = ++scheduleChangeId
  localForm.value.shifts = []
  localForm.value.uploadedBy = null
  localForm.value.monitoringType = ''
  localForm.value.monitoringTypeLabel = ''
  allocators.value = []
  allocatorsLoadFailed.value = false

  if (!val?.length) return

  const scheduleIds = val.map(Number)
  setMonitoringTypeFromCache()

  isLoadingAllocators.value = true
  try {
    await Promise.all([
      fetchAllocatorsFromSchedules(scheduleIds),
      fetchShifts(scheduleIds, currentChangeId)
    ])
    if (isStaleChange(currentChangeId)) return

    resolveMonitoringType()
    autoSelectLoggedAllocator()
  } catch (error) {
    if (isStaleChange(currentChangeId)) return
    toast.error('Erro ao carregar os dados das escalas.')
    console.error(error)
  } finally {
    if (!isStaleChange(currentChangeId)) {
      isLoadingAllocators.value = false
    }
  }
}

/**
 * Empresa e Unidade chegam preenchidas quando o wizard é aberto pelo "Enviar registro" da
 * vaga, mas `listHealthUnitsByCompany` só é carregada por `handleCompanyChange` — que é
 * handler de interação e nunca dispara num prefill. Sem lista, o select não acha o item e
 * mostra o id cru.
 *
 * NÃO reaproveitar `handleCompanyChange` aqui: ele zera `healthUnit`, `date` e os dependentes
 * de escala, apagando justamente o prefill que viemos hidratar.
 */
const hydratePrefilledCompanyAndUnit = async () => {
  const companyId = toId(localForm.value.company)
  if (!companyId) return

  if (!listCompanies.value.length) await companyStore.getMyCompanies()

  const unitAlreadyListed = listHealthUnitsByCompany.value.some((unit) =>
    isSameId(unit.value, localForm.value.healthUnit)
  )
  if (unitAlreadyListed) return

  isLoadingUnits.value = true
  try {
    await healthUnitStore.setHealthUnitsByCompany(companyId)
  } finally {
    isLoadingUnits.value = false
  }
}

// Quando StepForm é remontado (ex: navegando de volta do passo de arquivo), as listas
// locais de referência (activeModalities, allocators) ficam vazias enquanto os valores
// selecionados persistem no formulário pai. Sem refetch, v-autocomplete mostra IDs brutos.
onMounted(async () => {
  await hydratePrefilledCompanyAndUnit()

  const healthUnitId = localForm.value.healthUnit
  const referenceDate = getReferenceDateString()

  if (healthUnitId && referenceDate && !activeModalities.value.length) {
    isLoadingSchedules.value = true
    try {
      activeModalities.value = await Network.dsAttendance.getActiveModalities(
        healthUnitId,
        referenceDate
      )
    } catch {
      modalitiesLoadFailed.value = true
    } finally {
      isLoadingSchedules.value = false
    }
  }

  if (localForm.value.schedules?.length) {
    setMonitoringTypeFromCache()

    const scheduleIds = localForm.value.schedules.map(Number)
    const fetches: Promise<unknown>[] = []

    if (!allocators.value.length) {
      isLoadingAllocators.value = true
      fetches.push(
        fetchAllocatorsFromSchedules(scheduleIds).finally(() => {
          isLoadingAllocators.value = false
        })
      )
    }

    if (!listShifts.value.length) {
      isLoadingShifts.value = true
      fetches.push(
        shiftStore.setShiftsBySchedules(scheduleIds).finally(() => {
          isLoadingShifts.value = false
        })
      )
    }

    await Promise.all(fetches)
  }
})
</script>

<style scoped>
.form-step :deep(.v-field--variant-outlined) {
  border-radius: 4px;
}
.form-step :deep(.v-label) {
  color: #49454f;
  opacity: 1;
}
.form-step :deep(.v-selection-control__input) {
  --v-selection-control-size: 32px;
}
.date-field-error :deep(.v-label) {
  color: rgb(var(--v-theme-error));
}
.retry-link {
  all: unset;
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.75rem;
}
.retry-link:hover {
  filter: brightness(0.8);
}
.retry-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 2px;
}
</style>

<style>
.ds-attendance-schedules-dropdown .v-list-item--disabled {
  opacity: 0.45;
}
</style>
